<template>
  <section class="overflow-x-hidden py-8 lg:py-12">
    <div class="mx-auto w-full min-w-0 max-w-[1200px] px-4 sm:px-6 lg:px-0">
      <h2
        v-if="title"
        class="mb-6 break-words text-[32px] font-medium leading-10 tracking-[-0.02em] lg:text-[40px] lg:leading-[48px]"
      >
        {{ title }}
      </h2>
      <!-- body_html is sanitized server-side via HtmlSanitizer
           (Rails::HTML5::SafeListSanitizer); v-html is safe here. -->
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="rich-text-prose min-w-0 max-w-none overflow-x-hidden"
        v-html="bodyHtmlOrEmpty"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useTranslations } from '@/utils/translations.js';

const props = defineProps({
  title: { type: String, default: '' },
  body_html: { type: String, default: '' }, // eslint-disable-line vue/prop-name-casing
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
});

const { t } = useTranslations();

const bodyHtmlOrEmpty = computed(() => (
  props.body_html || `<p class="text-gray-400 italic">${t('rich_text.empty')}</p>`
));
</script>

<style scoped>
.rich-text-prose {
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
}

.rich-text-prose :deep(p) {
  margin-bottom: 1.25rem;
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: #374151;
}

.rich-text-prose :deep(p:empty),
.rich-text-prose :deep(p:has(> br:only-child)) {
  min-height: 1.75rem;
}

.rich-text-prose :deep(h1) {
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.75rem;
  font-weight: 500;
  line-height: 2.25rem;
  letter-spacing: -0.02em;
  color: #111827;
}

.rich-text-prose :deep(h2) {
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 2rem;
  letter-spacing: -0.02em;
  color: #111827;
}

.rich-text-prose :deep(h3) {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 1.875rem;
  letter-spacing: -0.02em;
  color: #111827;
}

.rich-text-prose :deep(h4) {
  margin-top: 1.25rem;
  margin-bottom: 0.625rem;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.75rem;
  letter-spacing: -0.02em;
  color: #111827;
}

.rich-text-prose :deep(h5),
.rich-text-prose :deep(h6) {
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.625rem;
  color: #111827;
}

@media (min-width: 1024px) {
  .rich-text-prose :deep(h1) {
    margin-top: 2.5rem;
    margin-bottom: 1.25rem;
    font-size: 2.25rem;
    line-height: 2.75rem;
  }

  .rich-text-prose :deep(h2) {
    margin-top: 2.5rem;
    margin-bottom: 1.25rem;
    font-size: 1.875rem;
    line-height: 2.375rem;
  }

  .rich-text-prose :deep(h3) {
    margin-top: 2rem;
    margin-bottom: 1rem;
    font-size: 2rem;
    line-height: 2.5rem;
  }

  .rich-text-prose :deep(h4) {
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    font-size: 1.75rem;
    line-height: 2.25rem;
  }

  .rich-text-prose :deep(h5),
  .rich-text-prose :deep(h6) {
    font-size: 1.25rem;
    line-height: 1.75rem;
  }
}

.rich-text-prose :deep(a) {
  color: #0369a1;
  text-decoration: underline;
  text-underline-offset: 0.12em;
  text-decoration-thickness: 2px;
}

.rich-text-prose :deep(a:has(span[style*="color"])) {
  text-decoration: none;
}

.rich-text-prose :deep(a span[style*="color"]),
.rich-text-prose :deep(a[style*="color"]) {
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-underline-offset: 0.12em;
  text-decoration-thickness: 2px;
}

.rich-text-prose :deep(ul),
.rich-text-prose :deep(ol) {
  margin-bottom: 1.25rem;
  padding-left: 1.5rem;
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: #374151;
}

.rich-text-prose :deep(li) {
  margin-bottom: 0.5rem;
}

.rich-text-prose :deep(blockquote) {
  margin: 1.5rem 0;
  border-left: 4px solid #e5e7eb;
  padding-left: 1.25rem;
  font-size: 1.125rem;
  font-style: italic;
  line-height: 1.75rem;
  color: #374151;
}

.rich-text-prose :deep(img:not([class*="rt-img-"])) {
  margin: 1.5rem 0;
  width: 100%;
  max-width: 100%;
}

.rich-text-prose :deep(code) {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.rich-text-prose :deep(pre) {
  max-width: 100%;
  overflow-x: auto;
  border-radius: 0.5rem;
  background: #f3f4f6;
  padding: 1rem;
}

.rich-text-prose :deep(pre code) {
  white-space: pre-wrap;
}

.rich-text-prose :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
}

.rich-text-prose :deep(strong:not(span[style*="color"] *)) {
  font-weight: 700;
  color: #111827;
}

.rich-text-prose :deep(span[style*="color"] :is(strong, em, u, s, code)) {
  color: inherit;
}

@media (max-width: 1023px) {
  .rich-text-prose :deep(img[class*="rt-img-"]) {
    float: none !important;
    display: block;
    width: 100% !important;
    max-width: 100% !important;
    margin: 1rem 0 !important;
  }
}
</style>
